import { useState } from "react";
import { Button } from "@common/Button";
import { ComponentColor, ComponentSize } from "@common/types";
import type { OptionValueDto } from "@models/option";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";

interface OptionValuePanelProps {
    values: OptionValueDto[];
    isProcessing: boolean;
    /** Id of the value whose delete is pending confirmation, if any. */
    pendingDeleteId: number | null;
    onCreate: (value: string) => void;
    onRequestDelete: (value: OptionValueDto) => void;
    onConfirmDelete: () => void;
    onCancelDelete: () => void;
}

export const OptionValuePanel = ({
    values,
    isProcessing,
    pendingDeleteId,
    onCreate,
    onRequestDelete,
    onConfirmDelete,
    onCancelDelete,
}: OptionValuePanelProps) => {
    const [newValue, setNewValue] = useState("");

    const handleAdd = () => {
        const trimmed = newValue.trim();
        if (!trimmed) return;
        onCreate(trimmed);
        setNewValue("");
    };

    const pendingValue =
        pendingDeleteId !== null
            ? values.find((v) => v.id === pendingDeleteId)
            : undefined;

    return (
        <div className="flex h-full flex-col">
            <div className="flex flex-1 flex-wrap content-start gap-2 overflow-y-auto">
                {values.map((value) => (
                    <span
                        key={value.id}
                        className="badge badge-outline gap-1 text-neutral"
                    >
                        {value.value}
                        <button
                            type="button"
                            aria-label={`Remove ${value.value}`}
                            onClick={() => onRequestDelete(value)}
                        >
                            ✕
                        </button>
                    </span>
                ))}
            </div>
            {pendingValue && (
                <div className="mt-2">
                    <ConfirmDeleteDialog
                        label={`value "${pendingValue.value}"`}
                        isProcessing={isProcessing}
                        onConfirm={onConfirmDelete}
                        onCancel={onCancelDelete}
                    />
                </div>
            )}
            <div className="mt-3 flex gap-2 border-t border-base-200 pt-3">
                <input
                    aria-label="New value"
                    className="input input-sm input-bordered flex-1 text-neutral"
                    placeholder="Add New"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") handleAdd();
                    }}
                />
                <Button
                    color={ComponentColor.Primary}
                    size={ComponentSize.SM}
                    loading={isProcessing}
                    onClick={handleAdd}
                >
                    Add
                </Button>
            </div>
        </div>
    );
};
